// Fees → Activity. The full fee audit trail as readable sentences:
// who changed what, from which rate to which, and why. Policy detail
// pages deep-link here with ?scope=&id= to show one entity's history.
//
// Filtering model (all server-side, all paginated):
//   - "All": the global feed across every scope;
//   - a level segment (Categories / Markets / Users / Platform default):
//     scope-only filter — every change at that level;
//   - an entity deep-link (?scope=&id=): a single entity's trail.
// The audit endpoint filters by scope alone when scope_id is omitted, so
// there's no client-side windowing anymore.
//
// Currency used to be the one filter the server could not apply: a fee
// audit row recorded a RATE change, and a rate had no currency of its
// own. Since SM-510 a policy prices exactly one currency and the audit
// row records it, so `?currency=` narrows the QUERY like every other
// filter here — the total is the count of what was asked for, and no
// row is hidden after the fact.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  fetchCategories,
  fetchFeeAudit,
  fetchUsersByIDs,
  feeKeys,
  fetchRules,
  keys,
} from "@/lib/queries";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Skeleton } from "@/components/ui/Skeleton";
import { AuditList } from "@/components/fees/AuditList";
import { LoadFailed } from "@/components/fees/LoadFailed";
import { scopeName, type AuditScope } from "@/components/fees/policy";
import { useMarketNames } from "@/hooks/useCatalogNames";
import { FeeCurrencyFilter } from "@/components/fees/FeeCurrencyFilter";
import {
  feeCurrencySearch,
  parseFeeCurrencySearch,
  scopedCurrency,
  type FeeCurrencyParam,
} from "@/components/fees/feeCurrency";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";

type ScopeFilter = "ALL" | AuditScope;

interface ActivitySearch {
  scope?: AuditScope;
  id?: string;
  currency?: FeeCurrencyParam;
}

export const Route = createFileRoute("/_app/treasury/fees/activity")({
  validateSearch: (search: Record<string, unknown>): ActivitySearch => {
    const scope = ["GLOBAL", "CATEGORY", "MARKET", "USER"].includes(
      search.scope as string,
    )
      ? (search.scope as AuditScope)
      : undefined;
    const currency = parseFeeCurrencySearch(search.currency);
    return {
      scope,
      id: scope && typeof search.id === "string" ? search.id : undefined,
      ...(currency ? { currency } : {}),
    };
  },
  component: FeeActivityPage,
});

const PAGE = 50;
// Cap for the name-lookup fetches (categories / markets). Matches the
// pagination caps used elsewhere in the console.
const LOOKUP_LIMIT = 200;

const FILTER_OPTIONS: { value: ScopeFilter; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "GLOBAL", label: "Platform default" },
  { value: "CATEGORY", label: "Categories" },
  { value: "MARKET", label: "Markets" },
  { value: "USER", label: "Users" },
];

function FeeActivityPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);

  const currencyParam = search.currency;
  const currency = scopedCurrency(currencyParam);
  // Only an answer when a currency is scoped: useCurrencyName falls
  // back to Gold's name for a null code.
  const scopedName = useCurrencyName(currency ?? null);
  const currencyName = currency ? scopedName : null;
  const filter: ScopeFilter = search.scope ?? "ALL";
  const scopeId = search.id;
  // A deep-link pins one entity (scope + id); a level segment pins a
  // scope; "All" pins nothing. All three filter on the server.
  const entityMode = Boolean(search.scope && scopeId);
  // undefined for "All"; the level (or entity scope) otherwise.
  const scopeParam: AuditScope | undefined =
    filter === "ALL" ? undefined : filter;

  useEffect(() => {
    setPage(0);
  }, [filter, scopeId, currencyParam]);

  const auditQ = useQuery({
    queryKey: feeKeys.audit(scopeParam, scopeId, PAGE, page * PAGE, currencyParam),
    queryFn: () =>
      fetchFeeAudit({
        limit: PAGE,
        offset: page * PAGE,
        scope: scopeParam,
        // scope_id only for an entity deep-link; a level filter sends
        // scope alone and lets the server filter the whole level.
        scope_id: entityMode ? scopeId : undefined,
        currency: currencyParam,
      }),
    staleTime: 15_000,
  });

  const items = auditQ.data?.items ?? [];
  const total = auditQ.data?.page.total ?? 0;

  // Lookup data for names (shared cache keys with the Policies pages).
  const categoriesQ = useQuery({
    queryKey: keys.categoriesList(LOOKUP_LIMIT, 0),
    queryFn: () => fetchCategories({ limit: LOOKUP_LIMIT, offset: 0 }),
  });
  const marketsQ = useQuery({
    queryKey: keys.rulesList(
      "OPEN",
      LOOKUP_LIMIT,
      0,
      { by: "created_at", dir: "desc" },
      {},
    ),
    queryFn: () =>
      fetchRules({
        limit: LOOKUP_LIMIT,
        offset: 0,
        status: "OPEN",
        sort: { by: "created_at", dir: "desc" },
      }),
  });
  const categories = categoriesQ.data?.items ?? [];
  const markets = marketsQ.data?.items ?? [];

  const marketNames = useMarketNames(
    useMemo(
      () => items.filter((a) => a.scope === "MARKET").map((a) => a.scope_id),
      [items],
    ),
    useMemo(() => new Map(markets.map((m) => [m.market_id, m.name])), [markets]),
  );

  const userIds = useMemo(
    () => [
      ...new Set(
        items
          .filter((a) => (a.scope as AuditScope) === "USER")
          .map((a) => a.scope_id),
      ),
    ],
    [items],
  );
  const usersQ = useQuery({
    queryKey: ["admin-fees", "audit-user-labels", userIds.join(",")] as const,
    queryFn: () => fetchUsersByIDs(userIds),
    enabled: userIds.length > 0,
    staleTime: 60_000,
  });
  const userMap = useMemo(
    () => new Map((usersQ.data ?? []).map((u) => [u.user_id, u])),
    [usersQ.data],
  );

  const nameFor = (scope: AuditScope, id: string): string => {
    if (scope === "MARKET") return marketNames.get(id) ?? id;
    if (scope === "USER") {
      const u = userMap.get(id);
      return u?.display_name || u?.email || id;
    }
    return scopeName(scope, id, categories, markets);
  };

  const filteredName = entityMode
    ? nameFor(search.scope as AuditScope, scopeId!)
    : null;

  // The currency scope survives a level change: it is a property of the
  // page, not of the level segment the operator happens to be on.
  const setFilter = (next: ScopeFilter) => {
    setPage(0);
    navigate({
      to: "/treasury/fees/activity",
      search: {
        ...(next === "ALL" ? {} : { scope: next }),
        ...feeCurrencySearch(currencyParam),
      },
      replace: true,
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Fee activity</h1>
        <p className="max-w-[75ch] text-sm text-muted">
          Every fee change, with who made it and why. Notes come from the
          &quot;Why this change?&quot; field on each save. Each row names the
          currency its policy prices; rows marked for every currency were set
          before fees were split by currency.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl<ScopeFilter>
          options={FILTER_OPTIONS}
          value={filter}
          onChange={setFilter}
        />
        <FeeCurrencyFilter
          className="w-48"
          value={currencyParam}
          onChange={(next) =>
            navigate({
              to: "/treasury/fees/activity",
              search: {
                ...(filter === "ALL" ? {} : { scope: filter }),
                ...(entityMode && scopeId ? { id: scopeId } : {}),
                ...feeCurrencySearch(next),
              },
              replace: true,
            })
          }
        />
        {filteredName && (
          <button
            type="button"
            onClick={() => setFilter(filter)}
            className="inline-flex items-center gap-1.5 rounded-full border border-line-1 bg-bg-2 px-2.5 py-1 text-2xs text-t-2 hover:border-line-3 hover:text-fg"
            title="Show all activity at this level"
          >
            Only: {filteredName}
            <span aria-hidden>×</span>
          </button>
        )}
      </div>

      {auditQ.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : auditQ.isError ? (
        <LoadFailed
          what="fee activity"
          error={auditQ.error}
          onRetry={() => void auditQ.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={currencyName ? `No fee activity in ${currencyName}` : "No fee activity"}
          description={
            currencyName
              ? `No policy that prices ${currencyName} has been changed at this level yet.`
              : filter === "ALL"
              ? "Changes to fee policies and user overrides will show up here."
              : "No changes recorded at this level yet."
          }
        />
      ) : (
        <AuditList items={items} nameFor={nameFor} />
      )}

      {auditQ.isSuccess && total > PAGE && (
        <Pagination
          page={{
            total,
            limit: PAGE,
            offset: page * PAGE,
            has_more: (page + 1) * PAGE < total,
          }}
          currentPage={page}
          onPageChange={setPage}
          itemLabel="changes"
        />
      )}
    </div>
  );
}
